import CircularProgress from "@mui/material/CircularProgress";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Individ, Jaktdag } from "~/src/api/types";
import AlertDefault, { AlertVariant } from "~/src/components/Alerts/AlertDefault";
import AlertWrapper from "~/src/components/Alerts/AlertWrapper";
import Text, { Header, TextVariant } from "~/src/components/Text/Text";
import { StorageContext } from "~/src/contexts/StorageContext/StorageProvider";
import { formatDmy, toStringWithFullMonth } from "~/src/helpers/time";
import { useClientState } from "~/src/hooks/useClientState";
import { useStateful } from "~/src/hooks/useStateful";
import AppDataContext from "../../../contexts/AppDataContext/DataProvider";
import { ArtType } from "../../../helpers/types";
import { getIndividQuery, getJaktdagQuery } from "../../../react-query/queries";
import TomtResultat from "../../Oversikt/Components/TomtResultat";
import IndividForm from "../components/IndividForm";
import { IndividData } from "../schemas/schema";

export default function FellingsInfo(): JSX.Element {
  const { t } = useTranslation();
  const { individId } = useParams();
  const { jaktfelt, vald, villreinvald, sync } = useContext(AppDataContext);
  const storage = useContext(StorageContext);
  const individ = useStateful<Individ | undefined>(undefined);
  const individErrors = useStateful<Partial<Record<keyof Individ, string>> | undefined>(undefined);
  const clientState = useClientState();
  const isOnline = clientState === "Online";
  const queryClient = useQueryClient();

  const individResult = useQuery({
    queryKey: ["individId-" + individId, { individId: individId }],
    queryFn: getIndividQuery,
    enabled: !!individId && storage.get<Individ>({ id: individId }, "Individ") === undefined,
    staleTime: 1000,
    retry: 1,
  });

  useEffect(() => {
    if (sync.didTrySyncWithItems.value || !individ) {
      individResult.refetch();
    }
  }, [sync.didTrySyncWithItems.value, isOnline]);

  useEffect(() => {
    // use stored individ if it exists
    // (newly created individ only exists in storage, and edited should use stored even if API is available)
    const storedIndivid = storage.get<Individ>({ id: individId }, "Individ");
    const jaktdagId =
      storedIndivid?.data.JaktdagId ?? individResult.data?.JaktdagId ?? individ.value?.JaktdagId;
    const storedJaktdag = storage.get<Jaktdag>({ id: jaktdagId }, "Jaktdag");
    const apiJaktdag = queryClient.getQueryData([
      "jaktdagId-" + jaktdagId,
      { jaktdagId: jaktdagId },
    ]);

    if (storedIndivid && storedIndivid.operation !== "delete") {
      individ.set({
        ...storedIndivid?.data,
        Jaktdag: storedJaktdag?.data ?? apiJaktdag ?? storedIndivid.data.Jaktdag,
      });
      individErrors.set(storedIndivid?.errors);
    } else if (individResult.isSuccess && typeof individResult.data !== "string") {
      individ.set({
        ...(individResult.data as Individ),
        Jaktdag: storedJaktdag?.data ?? apiJaktdag ?? individResult.data.Jaktdag,
      });
    }
  }, [individResult.data]);

  const currentOmrade = useMemo(() => {
    if (!individ) return null;

    switch (individ.value?.Art) {
      case ArtType.Hjort:
      case ArtType.Elg:
        return jaktfelt.data?.data.find((felt) => felt.Id === individ.value?.Jaktfelt);
      case ArtType.Villrein:
        return villreinvald.data?.data.find((vald) => vald.Id === individ.value?.Villreinvald);
      case ArtType.Rådyr:
        return vald.data?.data.find((vald) => vald.Id === individ.value?.Vald);
      default:
        return null;
    }
  }, [individ, jaktfelt, villreinvald, vald]);

  const jaktdag = useQuery({
    queryKey: ["jaktdag", { jaktdagId: individ.value?.JaktdagId }],
    queryFn: getJaktdagQuery,
    enabled:
      !!individ?.value?.JaktdagId &&
      storage.get<Jaktdag>({ id: individ?.value?.JaktdagId }, "Jaktdag") === undefined,
    staleTime: 0,
  });

  useEffect(() => {
    if (jaktdag.status == "success" && jaktdag.data) {
      individ.set({ ...individ.value, Jaktdag: jaktdag.data as Jaktdag } as Individ);
    } else if (storage.get<Jaktdag>({ id: individ?.value?.JaktdagId }, "Jaktdag") != undefined) {
      individ.set({
        ...individ.value,
        Jaktdag: storage.get<Jaktdag>({ id: individ?.value?.JaktdagId }, "Jaktdag")?.data,
      } as Individ);
    }
  }, [jaktdag.status]);

  if ((individResult.isLoading || individResult.isFetching) && !individ.value) {
    return (
      <TomtResultat>
        <CircularProgress thickness={7} size={80} />
      </TomtResultat>
    );
  }

  if (!individ.value) {
    return <TomtResultat>{t("errors.notFound", { type: "Individ" })}</TomtResultat>;
  }

  if (individ?.value?.Godkjent) {
    return (
      <div className=" h-full items-center justify-center flex mx-4">
        <AlertDefault variant={AlertVariant.Info}>{t("components.dyretErGodkjent")}</AlertDefault>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="mx-4">
        <div className="mt-6 mb-4">
          <Header headerLvl={2} serif variant={TextVariant.xlarge}>
            {currentOmrade?.Navn} {currentOmrade?.Id}
          </Header>
          <Text>
            {formatDmy(individ?.value?.Dato || individ.value?.Dato)
              ? `${t("pages.fellingsinfo.skutt")}: ${toStringWithFullMonth(
                  individ?.value?.Dato || individ.value?.Dato,
                  true,
                )}`
              : ""}
          </Text>
        </div>
        <AlertWrapper>
          {jaktdag?.data?.Godkjent && (
            <AlertDefault variant={AlertVariant.Success}>
              {individ.value?.Art === ArtType.Hjort
                ? t("components.jaktdagenErGodkjentHjort")
                : t("components.jaktdagenErGodkjent")}
            </AlertDefault>
          )}
        </AlertWrapper>
      </div>
      <IndividForm
        key={`${individId}-${JSON.stringify(individ)}`}
        initialInfo={individ.value as IndividData}
        mode="update"
        jaktdagGodkjent={jaktdag.data?.Godkjent}
        errors={individErrors.value}
      />
    </div>
  );
}
